/**
 * Team → Team plan — prototype `plan` (docs/prototype.html 3106): every open task for each person. What the
 * Ops associate director sets here (Do first / Do next / Later, and the order inside each) becomes the order
 * on that person's day.
 *
 * Data: GET /tasks (open, every page) grouped by assignee; GET /me/plan?user_id= per person for "plan
 * confirmed"; GET /timesheets?view=day for "timesheet in" when the caller may read the team's timesheets.
 * Writes: PATCH /tasks/{id} with If-Match — head_priority (resets manual_order), and manual_order renumbered
 * 10, 20, 30… inside a lane for ▲▼.
 */
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type { HeadPriority, Task, UserRef } from '@amplispot/contracts';
import { call } from '../../api';
import { useUsers } from '../../lib/data';
import { firstName, todayIso } from '../../lib/format';
import { useSession } from '../../lib/session';
import { dueText, refreshAfterWrite, useAllTasks, wkeys } from '../../lib/work';
import { Avatar, Empty, ErrorState, Loading, errorText, useToast } from '../../ui/kit';
import { ClientLogo } from '../clients/parts';

const LANES: [HeadPriority, string][] = [
  ['first', 'Do first'],
  ['next', 'Do next'],
  ['later', 'Later'],
];
const HP_LABEL: Record<HeadPriority, string> = { first: 'Do first', next: 'Do next', later: 'Later' };

/** Inside a lane: the manager's order, then most overdue, then soonest due. */
function byLane(a: Task, b: Task): number {
  const ao = a.manual_order ?? Number.MAX_SAFE_INTEGER;
  const bo = b.manual_order ?? Number.MAX_SAFE_INTEGER;
  if (ao !== bo) return ao - bo;
  if (a.days_overdue !== b.days_overdue) return b.days_overdue - a.days_overdue;
  if (a.due !== b.due) return a.due.localeCompare(b.due);
  return a.title.localeCompare(b.title);
}

const waLink = (phone?: string) => {
  const d = (phone ?? '').replace(/\D/g, '');
  return d.length >= 8 ? `https://wa.me/${d}` : null;
};

export function TeamPlan() {
  const { can, me } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const [sp, setSp] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const today = todayIso();
  const manager = can('tasks.read.all');

  const tasks = useAllTasks({ status: 'open' }, manager);
  const users = useUsers(manager);
  const ts = useQuery({
    queryKey: ['work', 'tsday', today],
    queryFn: () => call('timesheets.grid', { query: { view: 'day', from: today, to: today } }),
    enabled: can('timesheet.read.all'),
  });

  // Everyone who does ops work: the associate director first, then members; plus anyone holding open tasks.
  const people = useMemo(() => {
    const m = new Map<string, UserRef>();
    for (const u of users.data ?? []) if (u.status === 'active' && (u.role === 'ops_ad' || u.role === 'ops_member')) m.set(u.id, u);
    for (const t of tasks.data ?? []) if (t.assignee && !m.has(t.assignee.id)) m.set(t.assignee.id, t.assignee);
    const rank = (u: UserRef) => (u.role === 'ops_ad' ? 0 : u.role === 'ops_member' ? 1 : 2);
    return [...m.values()].sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
  }, [users.data, tasks.data]);

  const byPerson = useMemo(() => {
    const m = new Map<string, Task[]>();
    for (const t of tasks.data ?? []) if (t.assignee) m.set(t.assignee.id, [...(m.get(t.assignee.id) ?? []), t]);
    return m;
  }, [tasks.data]);

  const plans = useQueries({
    queries: people.map((p) => ({ queryKey: wkeys.plan(p.id), queryFn: () => call('me.plan', { query: { user_id: p.id } }), enabled: manager })),
  });

  const sel = people.find((p) => p.id === sp.get('who')) ?? people[0];
  const pick = (id: string) => {
    const n = new URLSearchParams(sp);
    n.set('who', id);
    setSp(n, { replace: true });
  };

  /** Run PATCHes one after another (each with its own If-Match), then refresh once. */
  const patch = async (edits: { t: Task; body: { head_priority?: HeadPriority | null; manual_order?: number | null } }[], ok: string) => {
    if (!edits.length) return;
    setBusy(true);
    try {
      for (const e of edits) await call('tasks.update', { params: { id: e.t.id }, ifMatch: e.t.version, body: e.body });
      toast(ok);
    } catch (e) {
      toast(errorText(e));
    } finally {
      refreshAfterWrite(qc);
      setBusy(false);
    }
  };

  if (!manager) return <Empty title="Not open to you">Team plan is for the founder and the Ops associate director.</Empty>;
  if (tasks.isLoading || users.isLoading)
    return (
      <div className="panel">
        <Loading />
      </div>
    );
  if (tasks.error || users.error)
    return (
      <div className="panel">
        <ErrorState error={tasks.error ?? users.error} onRetry={() => void (tasks.refetch(), users.refetch())} />
      </div>
    );
  if (!people.length) return <Empty title="Nobody to plan for yet">Add Ops people in Settings → People.</Empty>;

  const mine = sel ? (byPerson.get(sel.id) ?? []) : [];
  const lanes = LANES.map(([k, l]) => ({ k, l, rows: mine.filter((t) => t.effective_head_priority === k).sort(byLane) }));
  const overdueNotFirst = mine.filter((t) => t.overdue && t.effective_head_priority !== 'first');
  const wa = waLink(sel?.phone);

  const move = (lane: Task[], i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= lane.length) return;
    const order = lane.map((_, n) => n * 10);
    [order[i], order[j]] = [order[j]!, order[i]!];
    const edits = lane.map((t, n) => ({ t, body: { manual_order: order[n]! } })).filter((e) => e.t.manual_order !== e.body.manual_order);
    void patch(edits, `Saved. It sets the order of ${firstName(sel!.name)}’s next plan; today’s times stay as planned`);
  };

  return (
    <>
      <div className="sub" style={{ margin: '0 0 14px' }}>
        Every open task for each person. Set what comes first, and it becomes the order on their day.
      </div>
      <div className="tiles" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))' }}>
        {people.map((p, i) => {
          const list = byPerson.get(p.id) ?? [];
          const od = list.filter((t) => t.overdue).length;
          const first = list.filter((t) => t.effective_head_priority === 'first').length;
          const plan = plans[i]?.data;
          const cell = ts.data?.grid.find((g) => g.user.id === p.id)?.cells[0];
          const tsIn = cell?.status === 'in' || cell?.status === 'late';
          const chip: [string, string] | null = plans[i]?.isLoading
            ? null
            : cell?.status === 'not_expected'
              ? ['', 'Off today']
              : plan?.confirmed_at && tsIn
                ? ['hot', 'Plan and timesheet in']
                : plan?.confirmed_at
                  ? ['warm', 'Plan only']
                  : tsIn
                    ? ['hot', 'Timesheet in']
                    : p.id === me.user.id
                      ? ['warm', 'Plan not confirmed']
                      : ['cold', 'Nothing yet'];
          return (
            <div
              key={p.id}
              className={`ptile${sel?.id === p.id ? ' on' : ''}`}
              role="button"
              tabIndex={0}
              aria-pressed={sel?.id === p.id}
              onClick={() => pick(p.id)}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && pick(p.id)}
            >
              <div className="split">
                <Avatar user={p} size={26} />
                <b style={{ fontSize: 15 }}>{firstName(p.name)}</b>
                <div className="grow" />
                {od ? <span className="chip cold">{od} overdue</span> : null}
              </div>
              <div style={{ display: 'flex', gap: 18 }}>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 800 }}>{list.length}</div>
                  <div className="sub" style={{ fontSize: 12 }}>
                    open
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 800 }}>{first}</div>
                  <div className="sub" style={{ fontSize: 12 }}>
                    do first
                  </div>
                </div>
              </div>
              {chip ? (
                <span className={`chip ${chip[0]}`} style={{ justifySelf: 'start' }}>
                  {chip[1]}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>

      {sel ? (
        <>
          <div className="split" style={{ marginBottom: 14 }}>
            <h2 style={{ fontSize: 18, fontWeight: 800 }}>{sel.name}</h2>
            {wa ? (
              <a className="btn" href={wa} target="_blank" rel="noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
                Message {firstName(sel.name)} on WhatsApp
              </a>
            ) : null}
            {overdueNotFirst.length ? (
              <button
                className="btn"
                disabled={busy}
                onClick={() =>
                  void patch(
                    overdueNotFirst.map((t) => ({ t, body: { head_priority: 'first' as const, manual_order: null } })),
                    `Overdue tasks are now Do first for ${firstName(sel.name)}`,
                  )
                }
              >
                Make {overdueNotFirst.length} overdue {overdueNotFirst.length === 1 ? 'task' : 'tasks'} Do first
              </button>
            ) : null}
            <div className="grow" />
          </div>
          {!mine.length ? (
            <div className="panel">
              <Empty title={`${firstName(sel.name)} has no open tasks`}>New tasks for them show here.</Empty>
            </div>
          ) : (
            <div className="cols c3" style={{ alignItems: 'start' }}>
              {lanes.map((lane) => (
                <div className="kcol" key={lane.k} style={lane.k === 'first' ? { background: 'var(--accent-bg)' } : undefined}>
                  <div className="kh">
                    <span className={`rank ${lane.k}`}>{lane.l}</span>
                    <span className="n">{lane.rows.length}</span>
                  </div>
                  {lane.rows.map((t, i) => {
                    const d = dueText(t.due, today);
                    return (
                      <div className="tcard" key={t.id} style={{ marginBottom: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                        {t.client ? <ClientLogo id={t.client.id} name={t.client.name} size={36} /> : null}
                        <div className="b" style={{ minWidth: 150 }}>
                          <div className="t" style={{ fontSize: 13.5 }}>
                            <Link to={`?${new URLSearchParams({ ...Object.fromEntries(sp), task: t.id })}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                              {t.title}
                            </Link>
                          </div>
                          <div className="m">
                            <span>
                              {t.client ? t.client.name : 'Internal'}{' '}
                              {t.client?.priority === 'high' ? (
                                <span className="pm" title="High priority client">
                                  ▲
                                </span>
                              ) : null}
                            </span>
                            <span className={`chip ${d.tone}`}>{d.label}</span>
                            {t.blocked ? <span className={`chip ${t.blocked.flagged ? 'cold' : 'warm'}`}>Blocked</span> : null}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                          <button className="btn sm ghost" title="Move up" aria-label={`Move ${t.title} up`} disabled={busy || i === 0} style={i === 0 ? { opacity: 0.3 } : undefined} onClick={() => move(lane.rows, i, -1)}>
                            ▲
                          </button>
                          <button
                            className="btn sm ghost"
                            title="Move down"
                            aria-label={`Move ${t.title} down`}
                            disabled={busy || i === lane.rows.length - 1}
                            style={i === lane.rows.length - 1 ? { opacity: 0.3 } : undefined}
                            onClick={() => move(lane.rows, i, 1)}
                          >
                            ▼
                          </button>
                          <select
                            className="in"
                            style={{ width: 'auto', padding: '5px 10px', borderRadius: 99, fontSize: 12.5 }}
                            value={t.effective_head_priority}
                            disabled={busy}
                            aria-label={`Priority for ${t.title}`}
                            onChange={(e) => {
                              const v = e.target.value as HeadPriority;
                              void patch([{ t, body: { head_priority: v, manual_order: null } }], `Saved as "${HP_LABEL[v]}". It shows on ${firstName(sel.name)}’s day now; today’s times stay as planned`);
                            }}
                          >
                            {LANES.map(([k, l]) => (
                              <option key={k} value={k}>
                                {l}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </>
      ) : null}
    </>
  );
}
